import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserForm = ({ user, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    department: '',
    role: 'viewer',
    status: 'active',
    timezone: 'America/New_York',
    permissions: {
      documentRead: true,
      documentWrite: false,
      documentDelete: false,
      userManagement: false,
      systemSettings: false,
      changeManagement: false,
      aiAnalysis: false,
      auditLogs: false
    },
    sendInvitation: true,
    generateQR: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user?.fullName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        department: user?.department || '',
        role: user?.role || 'viewer',
        status: user?.status || 'active',
        timezone: user?.timezone || 'America/New_York',
        permissions: user?.permissions || {
          documentRead: true,
          documentWrite: false,
          documentDelete: false,
          userManagement: false,
          systemSettings: false,
          changeManagement: false,
          aiAnalysis: false,
          auditLogs: false
        },
        sendInvitation: false,
        generateQR: false
      });
    } else {
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        department: '',
        role: 'viewer',
        status: 'active',
        timezone: 'America/New_York',
        permissions: {
          documentRead: true,
          documentWrite: false,
          documentDelete: false,
          userManagement: false,
          systemSettings: false,
          changeManagement: false,
          aiAnalysis: false,
          auditLogs: false
        },
        sendInvitation: true,
        generateQR: false
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];

  const departments = [
    'Human Resources',
    'Information Technology',
    'Finance',
    'Operations',
    'Marketing',
    'Sales',
    'Legal',
    'Quality Assurance',
    'Research & Development',
    'Customer Support'
  ];

  const rolePermissions = {
    admin: {
      documentRead: true,
      documentWrite: true,
      documentDelete: true,
      userManagement: true,
      systemSettings: true,
      changeManagement: true,
      aiAnalysis: true,
      auditLogs: true
    },
    manager: {
      documentRead: true,
      documentWrite: true,
      documentDelete: false,
      userManagement: true,
      systemSettings: false,
      changeManagement: true,
      aiAnalysis: true,
      auditLogs: true
    },
    editor: {
      documentRead: true,
      documentWrite: true,
      documentDelete: false,
      userManagement: false,
      systemSettings: false,
      changeManagement: false,
      aiAnalysis: true,
      auditLogs: false
    },
    viewer: {
      documentRead: true,
      documentWrite: false,
      documentDelete: false,
      userManagement: false,
      systemSettings: false,
      changeManagement: false,
      aiAnalysis: false,
      auditLogs: false
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: rolePermissions?.[role]
    }));
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [permission]: checked
      }
    }));
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      alert('Unable to reset password: User email is missing');
      return;
    }

    if (!window.confirm(`Send password reset email to ${user?.fullName || user?.email}?\n\nThe user will receive an email with instructions to create a new password.`)) {
      return;
    }

    try {
      setIsResettingPassword(true);
      // Import userService dynamically to avoid circular dependency
      const { userService } = await import('../../../services/userService');
      const result = await userService?.resetUserPassword(user?.id, user?.email);
      
      if (result?.success) {
        alert(`✅ Password Reset Email Sent\n\nA password reset email has been sent to ${user?.email}.\n\nThe user should:\n1. Check their email inbox (and spam folder)\n2. Click the reset password link\n3. Create a new password\n\nThe link will expire in 60 minutes.`);
      } else {
        alert('Failed to send password reset email. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Error sending password reset email: ' + (error?.message || 'Unknown error. Please check the user\'s email address and try again.'));
    } finally {
      setIsResettingPassword(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData?.phone && !/^\+?[\d\s\-\(\)]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Map form data to match service expectations
      const saveData = {
        fullName: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        department: formData?.department,
        role: formData?.role,
        status: formData?.status,
        isActive: formData?.status === 'active',
        timezone: formData?.timezone,
        permissions: formData?.permissions,
        avatarUrl: formData?.avatarUrl,
        sendInvitation: formData?.sendInvitation,
        generateQR: formData?.generateQR
      };
      
      await onSave(saveData);
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">
            {user ? 'Edit User' : 'Create New User'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            iconName="X"
            iconSize={20}
          >
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Password Reset Section - Only for existing users */}
            {user && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-foreground">Password Management</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Reset user password and send them new credentials
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePasswordReset}
                    iconName="Key"
                    iconPosition="left"
                    disabled={isResettingPassword || isSubmitting}
                  >
                    {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                required
                value={formData?.fullName}
                onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                error={errors?.fullName}
                placeholder="Enter full name"
              />
              
              <Input
                label="Email Address"
                type="email"
                required
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                placeholder="user@company.com"
              />
              
              <Input
                label="Phone Number"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                error={errors?.phone}
                placeholder="+1 (555) 123-4567"
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Department
                </label>
                <select
                  value={formData?.department}
                  onChange={(e) => handleInputChange('department', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="">Select Department</option>
                  {departments?.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Role and Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Role
                </label>
                <select
                  value={formData?.role}
                  onChange={(e) => handleRoleChange(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData?.status}
                  onChange={(e) => handleInputChange('status', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Timezone
                </label>
                <select
                  value={formData?.timezone}
                  onChange={(e) => handleInputChange('timezone', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  {timezones?.map(tz => (
                    <option key={tz?.value} value={tz?.value}>{tz?.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Permissions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Checkbox
                    label="Document Read Access"
                    description="View and download documents"
                    checked={formData?.permissions?.documentRead}
                    onChange={(e) => handlePermissionChange('documentRead', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Document Write Access"
                    description="Create and edit documents"
                    checked={formData?.permissions?.documentWrite}
                    onChange={(e) => handlePermissionChange('documentWrite', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Document Delete Access"
                    description="Delete documents and folders"
                    checked={formData?.permissions?.documentDelete}
                    onChange={(e) => handlePermissionChange('documentDelete', e?.target?.checked)}
                  />
                  <Checkbox
                    label="AI Analysis Access"
                    description="Use AI analysis features"
                    checked={formData?.permissions?.aiAnalysis}
                    onChange={(e) => handlePermissionChange('aiAnalysis', e?.target?.checked)}
                  />
                </div>
                <div className="space-y-3">
                  <Checkbox
                    label="User Management"
                    description="Manage user accounts and permissions"
                    checked={formData?.permissions?.userManagement}
                    onChange={(e) => handlePermissionChange('userManagement', e?.target?.checked)}
                  />
                  <Checkbox
                    label="System Settings"
                    description="Configure system settings"
                    checked={formData?.permissions?.systemSettings}
                    onChange={(e) => handlePermissionChange('systemSettings', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Change Management"
                    description="Approve and manage change requests"
                    checked={formData?.permissions?.changeManagement}
                    onChange={(e) => handlePermissionChange('changeManagement', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Audit Logs"
                    description="View system audit logs"
                    checked={formData?.permissions?.auditLogs}
                    onChange={(e) => handlePermissionChange('auditLogs', e?.target?.checked)}
                  />
                </div>
              </div>
            </div>

            {/* Onboarding Options (only for new users) */}
            {!user && (
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">Onboarding Options</h3>
                <div className="space-y-3">
                  <Checkbox
                    label="Send Email Invitation"
                    description="Send welcome email with login instructions"
                    checked={formData?.sendInvitation}
                    onChange={(e) => handleInputChange('sendInvitation', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Generate QR Code"
                    description="Create QR code for quick access setup"
                    checked={formData?.generateQR}
                    onChange={(e) => handleInputChange('generateQR', e?.target?.checked)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
            >
              {user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;